import { Vue, Component, Watch } from 'vue-property-decorator'
import { merge, round } from 'lodash'
import * as Sentry from '@sentry/vue'
import { ServerModule } from 'shared/state'
import { Collection } from 'shared/types'
import {
	Machine, RaceState, RaceDefinition, RaceStatus, RaceStatusData,
	RaceData, RaceResults, RaceDataSplit, MachineType,
	WorkoutDefinition, WorkoutDataEntry, WorkoutData
} from 'shared/types/erg'
import { makeComputedBinding, sortByKeyAsc, sortByKeyDesc } from 'shared/util'
import { initialCap, formatTime, sanitizeDefinitionForSentry } from 'shared/util'

// Maps a progress-based duration_type to the machine field that tracks it live.
// wattminutes is the one duration_type whose wire/machine field name (watt_minutes)
// doesn't match the duration_type string itself.
const progressFieldMap = {
	meters: 'meters',
	calories: 'calories',
	wattminutes: 'watt_minutes'
}

@Component
class ErgModule extends Vue {
	get unit(): string {
		const def = this.definition
		if (!def) { return 'meters' }
		if (def.duration_type === 'calories' || def.race_type.includes('calorie')) {
			return 'calories'
		}
		if (def.duration_type === 'wattminutes' || def.race_type.includes('watt')) {
			return 'watt_minutes'
		}
		return 'meters'
	}

	get isPaceRace(): boolean {
		return this.definition ? this.definition.boats.filter(b => b.is_paceboat).length > 0 : false
	}

	get isTugRace(): boolean {
		return this.definition?.race_type === 'erg battle'
	}
	get isEliminationOrPaceRace() {
		return Erg.isPaceRace || !!Erg.definition?.race_type.includes('elimination')
	}
	get stopBoatsWhenPassed(): boolean {
		return !!this.definition!.stop_boat_when_passed_by_paceboat
	}

	get serverState() { return this.server ? this.server.state : 'unconnected' }

	status: RaceState = 'inactive'
	lanes: RaceStatusData[] = []
	definition: RaceDefinition | null = null
	machines: Machine[] = []
	paceboats: Collection<Machine> = {}
	// paceraceLockScore: boolean = true
	classes: string[] = []
	time = 0
	bufferedTime = 0
	totalElapsed = 0
	numFinished = 0
	supportedThemes: string[] = []
	supportedLanguages: string[] = []
	paceraceFinished: boolean = false
	paceraceBufferedFinish: boolean = false
	isTimeCapRace: boolean = false
	results: any = []
	countDownTimeCaps = true


	elimination_rounds = 0
	elimination_count_per_round = 0
	current_elimination_round = 1
	eliminations_this_round = 0
	eliminatedLanes: number[] = []
	segmentDuration = 0
	workoutDefinition: WorkoutDefinition | null = null
	workoutData: WorkoutDataEntry[] = []
	finalRaceSnapshot: any = null
	// Set once this client has seen the race clock advance between two race_data packets,
	// i.e. it is watching the current race live. Reset on every race_definition.
	sawLiveRace = false
	private firstRaceDataTime: number | null = null

	/**
	 * represents the number of meters of the fastest at risk machine during an elimination round
	 */
	public get eliminationThreshold() {
		if (!Erg.definition || !this.atRisk.length) { return 0 }
		return this.atRisk[0].meters || 0
	}

	get atRisk(): Machine[] {
		return this.machines.slice().filter(r => r.is_at_risk).sort((a, b) => {
			return sortByKeyAsc(a, b, 'position')
		})
	}

	get slowestNonAtRiskMeters(): number {
		if (!Erg.definition || !this.atRisk.length) { return 0 }
		return this.machines.slice().filter(r => !r.is_at_risk && !r.is_eliminated).sort((a, b) => {
			return sortByKeyDesc(a, b, 'position')
		})[0].meters || 0
	}

	private server: ServerModule | null = null
	private subscription: number | null = null

	attachToServer(server: ServerModule) {
		this.server = server
	}
	clearDefinition() {
		this.definition = null
		this.workoutDefinition = null
		this.workoutData = []
		this.elimination_rounds = 0
		this.elimination_count_per_round = 0
		this.current_elimination_round = 1
		while (this.eliminatedLanes.length > 0) {
			this.eliminatedLanes.pop()
		}
		while (this.machines.length > 0) {
			this.machines.pop()
		}
	}

	raceResults() {
		// const result = null
		// tslint:disable-next-line
		// try {localStorage.getItem('raceResults') || this.results} catch {}
		// if (result) { return JSON.parse(result) }
		return this.machines
			.filter(m => !m.is_paceboat)
			.map(m => {

				const avg_pace = (this.unit === 'calories')
					? m.avg_calories_per_hour || ''
					: m.avg_pace ? formatTime(m.avg_pace, 1) : ''

				const row = {
					Place: m.position,
					Lane: m.lane,
					Name: m.name,
					'Athlete ID': m.affiliation,
					'Average Pace': avg_pace,
					'Average Watts': m.avg_watts,
					Watts: m.watts ? m.watts : '',
					['Cumulative ' + initialCap(this.unit)]: Math.floor(Erg.definition!.duration_type === 'time' ?
						m[this.unit]! : m[this.unit]!).toFixed(0),
					[initialCap(this.unit)]: Math.floor(Erg.definition!.duration_type === 'time' ? m[this.unit]!
						: Erg.definition!.duration - m[this.unit]!).toFixed(0),
					Score: m.score
				}
				if (Erg.classes.length) {
					row['Place in Class'] = m.position_in_class,
						row.Class = m.class_name
				}
				return row
			})
	}
	public changeCountDownTimeCaps(newVal: boolean) {
		this.countDownTimeCaps = newVal

		if (!['race running - sound horn', 'race running'].includes(this.status)
			&& this.definition?.time_cap && this.definition.time_cap > 0) {
			const startTime = this.countDownTimeCaps ? this.definition.time_cap : 0
			this.bufferedTime = this.time = startTime
		}
	}

	private supported_languages(languageFiles: string[]) {
		this.supportedLanguages = languageFiles
	}

	private supported_themes(themeFiles: string[]) {
		this.supportedThemes = themeFiles
	}

	private race_definition(definition: RaceDefinition) {
		this.clearDefinition()
		if (definition.time_cap) { definition.time_cap = Number(definition.time_cap) }
		if (definition.segments) {
			this.segmentDuration = definition.segments[this.current_elimination_round - 1].duration
		}
		if (definition.duration_type === 'time') {
			definition.duration /= 100
			definition.split_value /= 100
		}

		let startTime = 0
		this.isTimeCapRace = false
		if (definition.time_cap && definition.time_cap > 0) {
			this.isTimeCapRace = true
			startTime = this.countDownTimeCaps ? definition.time_cap : 0
		}
		if (definition.duration_type === 'time') {
			startTime = definition.duration
		}
		this.bufferedTime = this.time = startTime

		this.totalElapsed = 0
		this.numFinished = 0
		this.paceraceFinished = this.paceraceBufferedFinish = false
		this.sawLiveRace = false
		this.firstRaceDataTime = null

		const machines: Machine[] = []
		const classes: Set<string> = new Set()
		definition.boats
			.filter(boat => boat.name !== 'EMPTY')
			.forEach((boat, i) => {
				classes.add(boat.class_name)
				const machine: Machine = {
					is_paceboat: boat.is_paceboat ? true : false,
					lane: boat.lane_number,
					affiliation: boat.affiliation,
					name: boat.name,
					class_name: boat.class_name,
					delta: 0,
					position: i + 1,
					position_in_class: i + 1,
					meters: definition.duration_type === 'meters' && boat.meters ?
						definition.duration - (boat.meters / 10) : 0,
					calories: definition.duration_type === 'calories' && boat.calories ?
						definition.duration - (boat.calories / 10) : 0,
					watt_minutes: definition.duration_type === 'wattminutes' && boat.watt_minutes ?
						definition.duration - (boat.watt_minutes / 10) : 0,
					gap: 0,
					meters_behind: 0,
					calories_behind: 0,
					pace: 0,
					avg_pace: 0,
					avg_watts: 0,
					calories_per_hour: 0,
					avg_calories_per_hour: 0,
					spm: 0,
					splits: [],
					time: 0,
					score: null,
					active_machine: boat.machine_type || 'unknown',
					erg_status: '1',
					disconnected: 0,
					finished: false,
					terminated: 0,
					participants: boat.participants,
					is_at_risk: 0,
					is_eliminated: 0,
					watts: 0,
					watt_minutes_behind: 0
				}
				if (boat.logbook_id) {
					machine.logbook_id = String(boat.logbook_id)
				}
				machines[boat.lane_number - 1] = machine
				if (machine.is_paceboat) {
					this.paceboats[machine.class_name] = machine
				}
			})
		this.machines = machines
		this.classes = Array.from(classes)

		this.definition = definition
		this.$emit('race_definition', definition)

		// Every error we've looked at so far (Polly, delta_to_win, the "too many
		// properties" RangeError) was reported with no idea what race was running
		// at the time — race_type, boat count, duration are never captured.
		// Attaching the definition closes that gap for future crash reports.
		// Skip when no client is attached (e.g. local debugging without Sentry.init()) —
		// Sentry itself is always truthy once imported, so check the client, not the module.
		if (Sentry.getClient()) {
			Sentry.setContext('race_definition', sanitizeDefinitionForSentry(definition))
		}
	}

	private race_data(data: RaceData) {
		if (!this.definition) { return }
		const { duration_type, duration } = this.definition!

		this.eliminations_this_round = data.eliminations_this_round || 0
		if (this.isPaceRace && this.stopBoatsWhenPassed && this.paceraceFinished) { return }
		if (!this.definition) { return }
		const raceType = this.definition.duration_type

		data.time /= 100
		// A display that joins after 'warmup' never clears the stored results of an earlier race,
		// and race_results would later restore that race's places onto this one. Once the clock
		// moves between two packets this race is running live, so nothing in storage can belong
		// to it. A single packet isn't enough: a display opened after a race finishes may be sent
		// the final packet, and that must not wipe the snapshot it is about to restore.
		if (this.firstRaceDataTime === null) {
			this.firstRaceDataTime = data.time
		} else if (!this.sawLiveRace && data.time !== this.firstRaceDataTime) {
			this.sawLiveRace = true
			this.clearStoredResults()
		}
		const elapsed = raceType === 'time'
			? this.time - data.time
			: this.isTimeCapRace && this.countDownTimeCaps ?
				data.time - (this.definition.time_cap! - this.time) : data.time - this.time
		this.totalElapsed += elapsed
		this.bufferedTime = this.time
		this.time = this.isTimeCapRace && this.countDownTimeCaps ? this.definition.time_cap! - data.time : data.time
		if (this.time < 0) {
			this.time = 0
		}
		let roundChanged = false
		if (this.definition.segments?.length) {
			const reportedRound = data.data.find(b => !b.is_eliminated).current_elimination_round || 1
			roundChanged = (reportedRound !== this.current_elimination_round)
			this.current_elimination_round = reportedRound
			if (roundChanged) {
				this.segmentDuration = this.definition.segments[this.current_elimination_round - 1].duration
			}
			this.segmentDuration -= elapsed
			if (this.segmentDuration < 0) { this.segmentDuration = 0 }
		}
		data.data.forEach(item => {
			const machine = this.machines[item.lane - 1]
			if (machine === undefined) { return }
			// Captured before machine.is_eliminated is reassigned below: the merge guard
			// further down must reflect whether this boat was ALREADY eliminated going into
			// this packet, not whether it becomes eliminated as a result of it — otherwise
			// the very packet that first reports elimination has its own (correct, final)
			// progress skipped, freezing machine.meters/calories/watt_minutes one packet too
			// early. Confirmed live (2026-10-19): a paceboat-stopped boat's scroll-view score
			// stuck one packet behind the leaderboard's other_score-derived value because of
			// this.
			const wasAlreadyEliminated = machine.is_eliminated
			if (item.meters) {
				item.meters /= 10
			}
			// if (item.delta) {
			// 	item.delta /= 10
			// }
			if (item.calories) {
				item.calories /= 10
			}
			// if (item.watts) {
			// 	item.watts /= 10
			// }
			if (item.watt_minutes) {
				item.watt_minutes /= 10
			}
			const key = progressFieldMap[duration_type as keyof typeof progressFieldMap]
			if (key && item[key] !== undefined) {
				item[key] = duration - item[key]!
				if (!machine.finished && item[key]! >= duration) {
					machine.finished = true
					this.numFinished++
				}
			}

			// progressFieldMap has no entry for duration_type 'time', so the progress
			// check above is a no-op for time-duration races — this is their only
			// finished detector. It must use the countdown-adjusted this.time (sic:
			// not the raw per-packet data.time, which is simply elapsed-since-start
			// and is 0 at the start of every race regardless of duration_type) and
			// must only apply to a time-capped race actually running in countdown
			// display mode — otherwise it fired on literally every race's first
			// packet, marking every boat "finished" before anyone had moved, which
			// bypassed the sortAfter-gated sort entirely (confirmed live, 2026-10-29).
			if (!machine.finished && this.isTimeCapRace && this.countDownTimeCaps && this.time === 0) {
				machine.finished = true
				this.numFinished++
			}
			machine.is_eliminated = item.is_eliminated
			if (item.is_eliminated && !this.eliminatedLanes.includes(item.lane)) {
				this.eliminatedLanes.push(item.lane)
				this.$emit('lane_eliminated', item.lane)
				if (!machine.finished) {
					machine.finished = true
					this.numFinished++
				}

			}
			machine.is_at_risk = item.is_at_risk
			machine.round_eliminated = item.round_eliminated
			machine.unsynced_meters = item.unsynced_meters

			item.pace = item.pace !== undefined ? item.pace / 100 : 0
			item.avg_pace = item.avg_pace !== undefined ? item.avg_pace / 100 : 0
			item.avg_watts = item.avg_watts !== undefined ? item.avg_watts : 0
			item.calories_per_hour = item.calories_per_hour || 0
			item.avg_calories_per_hour = item.avg_calories_per_hour || 0
			item.gap = item.gap !== undefined ? item.gap / 10 : 0
			item.meters_behind = item.meters_behind !== undefined ? item.meters_behind / 10 : 0
			item.calories_behind = item.calories_behind !== undefined ? item.calories_behind / 10 : 0
			// time is sent in 100ths/sec, but we only care about 10ths. also matches format sent by race_results
			item.time = item.time !== undefined ? Math.round(item.time / 10) / 10 : 0
			// Only transform splits that haven't been seen before.
			// Re-mapping all historical splits on every packet scales with race
			// duration (30 splits × 54 boats at hour 15 = ~3k objects/packet).
			if (item.splits !== undefined) {
				const existing = machine.splits ?? []
				const incoming = item.splits
				if (incoming.length > existing.length) {
					const transformed = incoming.slice(existing.length).map(s => ({
						split_time: s.split_time / 100,
						split_distance: s.split_distance ? s.split_distance / 10 : 0,
						split_pace: calculateSplitPace(s, machine.active_machine),
						split_calorie: s.split_calorie ? s.split_calorie / 10 : 0,
						split_watt_minute: s.split_watt_minute ? s.split_watt_minute / 10 : 0
					}))
					item.splits = existing.length ? [...existing, ...transformed] : transformed
				} else {
					item.splits = existing
				}
			} else {
				item.splits = []
			}
			if (machine.is_paceboat) {
				item.position_in_class = 0
				const field = Erg.unit === 'calories' ? 'calories_per_hour' : 'pace'
				if (item[field] !== machine[field] && item[field] !== 0) {
					this.$emit('paceboat_pace', item)
				}
			}

			if (this.stopBoatsWhenPassed && machine.is_eliminated) {
				machine.position_in_class = item.position_in_class
			}

			// if(!this.stopBoatsWhenPassed && machine.is_eliminated) {
			// 	// handle setting being turned off mid race
			// 	machine.is_eliminated = 0
			// 	if(machine.finished) {
			// 		machine.finished = false
			// 		machine.score = null
			// 		this.numFinished--
			// 	}
			// }

			if (!(this.stopBoatsWhenPassed && wasAlreadyEliminated)) {
				merge(machine, item)
				machine.disconnected = machine.erg_status === '0' ? machine.disconnected + 1 : 0
			}
		})

		// // After all distances have been updated, check whether paceboats have overtaken anything
		// if(this.isPaceRace && this.stopBoatsWhenPassed) {
		// 	const sorted = this.machines.slice().sort((a, b) => {
		// 		return sortByKeyDesc(a, b, this.unit, sortByKeyAsc(a, b, 'lane'))
		// 	})

		// 	sorted.forEach(machine => {
		// 		if(machine.is_paceboat) {
		// 			machine.position = machine.position_in_class = 0
		// 			return
		// 		}

		// 		if(machine.is_eliminated) { return }

		// 		const paceboat = this.paceboats[machine.class_name]
		// 		if(!paceboat) { return }
		// 		if(paceboat[this.unit]! > machine[this.unit]!) {
		// 			machine.is_eliminated = 1
		// 			machine.finished = true
		// 			machine.score = Math.round(machine.time / 10)
		// 		}
		// 	})
		// 	this.paceraceFinished = this.paceraceBufferedFinish
		// 	if(this.paceraceFinished) {
		// 		setTimeout( _ => {
		// 			this.pause()
		// 		}, 1000)
		// 	}
		// 	this.paceraceBufferedFinish =
		// this.numFinished + Object.keys(this.paceboats).length >= this.definition.boats.length
		// }

		// Live counterpart to the race_definition context: the specific data point
		// missing from every long-race investigation so far — how far progressed,
		// and how large the per-machine splits arrays had actually grown — without
		// which a future crash report can't be matched to a race length/stage.
		// Skip when no client is attached — see race_definition()'s guard above.
		if (Sentry.getClient()) {
			Sentry.setContext('race_state', {
				status: this.status,
				elapsed,
				numFinished: this.numFinished,
				sawLiveRace: this.sawLiveRace,
				maxSplitsPerMachine: this.machines.reduce(
					(max, m) => Math.max(max, m.splits?.length ?? 0), 0
				),
			})
		}

		this.$emit('race_data', this.machines, elapsed, roundChanged)

	}

	private race_status(status: RaceStatus) {
		if (this.status === status.state_desc && this.lanes === status.data) { return }
		if (status.state_desc === 'inactive') {
			// prevent white screens on desktop
			// this.definition = null
		}
		if (status.state_desc === 'warmup') {
			this.clearStoredResults()
		}
		this.status = status.state_desc
		if (status.data) { this.lanes = status.data }
		if (status.state_desc === 'final results') {
			// set the buffered time after delaying one second.

			setTimeout(_ => {
				this.bufferedTime = this.time
				if (this.isPaceRace) {
					this.paceraceFinished = this.paceraceBufferedFinish = true
				}
			}, 1000)
		}
		this.$emit('race_status', this.status)
	}

	private race_results(results: RaceResults) {
		if (!this.definition) { return }
		let storedResult = null
		try { storedResult = JSON.parse(localStorage.getItem('raceResults')) } catch { }
		results.data.forEach(r => {
			const machine = this.machines[r.lane - 1]
			if (!machine) { return }
			if (r.other_score) {
				machine.other_score = r.other_score
			}
			machine.score = this.definition!.duration_type !== 'time' ? r.score / 10 : r.score
			machine.avg_pace = r.avg_pace / 100 || 0
			if (this.definition!.duration_type !== 'time') {
				machine.time = r.score / 10
			}
			// ErgRace only sends `terminated` for specific cases (time-cap races, a pace boat
			// passing a stopped boat, biathlon/multi-activity partial results) — NOT for a
			// plain individual race manually aborted mid-race. In that common case every lane's
			// `terminated` is simply absent, even for boats that never reached the target. Fall
			// back to the client's own `machine.finished` tracking (set live in race_data the
			// moment a boat's progress reaches the race duration) so those boats still resolve
			// to the wasTerminated score type instead of silently defaulting to finishedNormally.
			//
			// Confirmed live (2026-10-19) that `r.terminated: 1` is NOT a reliable per-boat
			// "didn't reach the target" signal: real ErgRace sends it for EVERY boat whenever
			// a time cap is configured on the race, even one that genuinely finished (meters,
			// calories, and watt-minutes races all confirmed). So a boat that the client's own
			// live progress tracking already knows reached the full duration target is never
			// actually terminated, regardless of what the wire says.
			const duration_type = this.definition!.duration_type
			const progressField = progressFieldMap[duration_type as keyof typeof progressFieldMap]
			const reachedTarget = !!progressField && machine[progressField] != null
				&& machine[progressField]! >= this.definition!.duration
			machine.terminated = reachedTarget ? 0 : r.terminated ?? (!machine.finished ? 1 : 0)
			machine.avg_pace = r.avg_pace / 100 || 0
			if (!machine.finished && r.terminated) {
				machine.finished = true
				this.numFinished++
			}
			if (storedResult && this.time === 0) {
				const result = storedResult.find((res: any) => res.Lane === machine.lane)
				if (result) {
					machine.position = result.Place
					machine.position_in_class = result['Place in Class']
				}
			}
		})

		// 2. Snapshot restore — applied on every race_results message while a snapshot exists.
		// Purpose: if the page is refreshed or a new tab/iframe is opened after a race finishes
		// (or joins the websocket late, e.g. an embedded iframe), the server will re-send
		// race_results and this restores the final display state from localStorage rather than
		// showing incomplete live data (race_results itself carries no meters/gap fields).
		// The snapshot is ONLY ever cleared from storage when the next race enters warmup (see
		// race_status handler) or here if it is more than 24 hours old (stale from a previous
		// day's race) — a refresh must never delete it. If it belongs to a different race_id
		// (stale from an earlier/different race this client never saw the 'warmup' transition
		// for, e.g. a late-joining iframe) it is simply not applied, left untouched in storage
		// in case a client for the race it actually belongs to needs it later.
		let parsedSnap = null
		try {
			const snap = localStorage.getItem('finalRaceSnapshot')
			parsedSnap = snap ? JSON.parse(snap) : null
			if (parsedSnap && !Array.isArray(parsedSnap.machines)) { parsedSnap = null }
		} catch { parsedSnap = null }
		const ONE_DAY_MS = 24 * 60 * 60 * 1000
		const snapIsStale = parsedSnap && parsedSnap.savedAt && Date.now() - parsedSnap.savedAt > ONE_DAY_MS
		const snapBelongsToCurrentRace = parsedSnap && parsedSnap.raceId === (this.definition!.race_id || null)
		try {
			if (parsedSnap && snapIsStale) {
				localStorage.removeItem('finalRaceSnapshot')
				this.finalRaceSnapshot = null
			}
			if (parsedSnap && !snapIsStale && snapBelongsToCurrentRace) {
				const parsed = parsedSnap
				this.time = parsed.time
				this.bufferedTime = parsed.bufferedTime

				parsed.machines.forEach((saved: any) => {
					if (!saved) { return }
					const idx = saved.lane - 1
					const machine = this.machines[idx]
					if (!machine) { return }

					this.machines.splice(idx, 1, { ...machine, ...saved })
				})

				this.finalRaceSnapshot = parsed
				this.$emit('final-results-ready')
			}
		} catch {
			//
		}

		// 3. Emit AFTER restore
		try {
			this.$emit('race_results', results)
			this.results = results

			// 4. Capture a snapshot for THIS race if none was just restored above (covers: no
			// snapshot existed yet, it was stale and discarded, or it belonged to a different
			// race and was left in storage untouched — this race still needs its own snapshot)
			if (!this.finalRaceSnapshot && this.time > 0) {
				this.captureFinalSnapshot()
			}

			if (this.time > 0) {
				localStorage.setItem('raceResults', JSON.stringify(this.raceResults()))
			}
		} catch {
			//
		}

	}
	private clearStoredResults() {
		try {
			localStorage.removeItem('raceResults')
			localStorage.removeItem('finalRaceSnapshot')
			this.results = []
			this.finalRaceSnapshot = null
		} catch { }
	}
	private captureFinalSnapshot() {
		this.finalRaceSnapshot = {
			savedAt: Date.now(),
			raceId: this.definition!.race_id || null,
			time: this.time,
			bufferedTime: this.time,
			machines: this.machines.map(m => {
				const copy: any = {}
				for (const key in m) {
					if (
						m.hasOwnProperty(key) &&
						typeof m[key] !== 'function' &&
						key !== '__ob__' // Vue observer
					) {
						copy[key] = m[key]
					}
				}

				// Ensure pace-related fields are included if they exist
				// These may be missing or undefined at snapshot time

				const extraFields = [
					'pace',
					'avg_pace',
					'calories_per_hour',
					'avg_calories_per_hour'
				]

				extraFields.forEach(field => {
					if (m[field] !== undefined) {
						copy[field] = m[field]
					}
				})
				return copy
			})
		}

		localStorage.setItem('finalRaceSnapshot', JSON.stringify(this.finalRaceSnapshot))
	}

	private workout_data(data: WorkoutData) {
		this.workoutData = data.data
		this.$emit('workout_data', data)
	}

	private workout_definition(definition: WorkoutDefinition) {
		this.workoutDefinition = definition
		this.workoutData = []
		this.$emit('workout_definition', definition)
	}


	private created() {
		// send an empty race definition to set the race background
		setTimeout(() => {
			if (this.definition === null) {
				this.race_definition({
					boats: [],
					duration: 100,
					duration_type: 'meters',
					event_name: '',
					name_long: '',
					name_short: '',
					race_id: '',
					race_type: 'individual',
					split_value: 100,
					stop_boat_when_passed_by_paceboat: 0,
					time_cap: 0
				})
			}
		}, 2000)
	}

	private pause() {
		this.$emit('pause')
	}

	@Watch('serverState')
	private serverStateChange() {
		// the server might reconnect after its first connection, but we only want to add our listener once
		if (this.serverState === 'connected' && this.subscription == null) {
			this.subscription = this.server!.addListener((channel, packet) => {
				if (this[channel] && typeof this[channel] === 'function') {
					this[channel](packet)
				}
			})
		}
		if (this.serverState !== 'connected') {
			// this.definition = null
			if (['race running', 'race running - sound horn'].includes(this.status)) {
				this.machines.forEach(m => m.disconnected = 1)
				this.$emit('race_data', this.machines, 0.1)
			}
			this.status = 'inactive'
		}
	}
}

function calculateSplitPace(split: RaceDataSplit, type: MachineType) {
	const time = Math.floor(split.split_time / 10)
	const distance = Math.floor(split.split_distance ? split.split_distance / 10 : 0)
	const pace_distance = type === 'bike' ? 1000 : 500
	return distance === 0 ? 0 : Math.floor(pace_distance * time / distance) / 10
}


export const Erg = new ErgModule()
export const ErgState = makeComputedBinding(Erg)
